import { DualUploadComponent } from '..';

import { useEffect, useMemo, useRef, useState } from 'react';

import { ArrowDown01Icon, ArrowUp01Icon } from '@hugeicons/core-free-icons';
import cn from 'classnames';
import { ALLOWED_TYPES_MODE_ID } from 'constants/upload-allowed-types';
import { isAcceptedUpload } from 'helpers/candidate_application/id-requirements';
import { matchesRequired, parseIdKey } from 'helpers/ids';
import { useSelector } from 'react-redux';

import { DocumentUpload } from 'components/FormComponents/DocumentUpload/DocumentUpload';
import { FormMessage } from 'components/FormComponents/FormMessage/FormMessage';
import { MultipleDocumentUpload } from 'components/FormComponents/MultipleDocumentUpload/MultipleDocumentUpload';
import { IconComponent } from 'components/Icon/IconComponent';
import { StatusPill } from 'components/Pills/StatusPills';

import { selectCandidateApplication } from 'api/candidate-application-slice';

import styles from './IdRequirementsList.module.scss';

export const IdRequirementsList = ({
  code,
  uploadCode,
  brand,
  options = [],
  className,
  wrapperRequirementsMet = false,
  hasIdRequirements = false,
  requirementsMet = false,
  getUpload,
  signUpload,
  createUpload,
  deleteUpload,
  modelParams,
  min = 0,
  title,
  description,
  onPointsChange,
  setValue,
  rejectedData,
}) => {
  const {
    candidateFields,
    application: { id_uploads, other_uploads },
  } = useSelector(selectCandidateApplication);

  // Store the latest callback in a ref to avoid re-render loops
  const onPointsChangeRef = useRef(onPointsChange);
  onPointsChangeRef.current = onPointsChange;

  const [showList, setShowList] = useState(true);
  const [isVerifying, setIsVerifying] = useState(false);

  // Calculate current points from option.selected (same source of truth as id-requirements.js).
  // This keeps the bar in sync when IDs are assigned from other lists (e.g. pending assignment).
  const currentPoints = useMemo(() => {
    return options.reduce((total, option) => {
      if (!option.selected) return total;
      if (option.needsComplementaryDoc) return total;
      const p = Number.isFinite(option.points) ? option.points : 0;
      return total + p;
    }, 0);
  }, [options]);

  const handleVerifyState = state => {
    setIsVerifying(state);
  };

  // Notify parent component when points change
  useEffect(() => {
    if (onPointsChangeRef.current) {
      onPointsChangeRef.current(currentPoints);
    }
  }, [currentPoints]); // Only depend on the actual calculated points value

  // Compute filtered uploads - only include accepted IDs (verified, needs_review, or failed with >= 3 attempts)
  const idsUploadedOnList = useMemo(() => {
    return (
      id_uploads?.filter(upload => {
        const matchesOption = options.some(option =>
          matchesRequired(upload.metadata?.id_type, option.value),
        );
        if (!matchesOption) return false;
        return isAcceptedUpload(upload);
      }) || []
    );
  }, [id_uploads, options]);

  const idsUploadedOnListNotDisabled = useMemo(() => {
    return (idsUploadedOnList || [])?.filter(upload =>
      options.some(
        option => matchesRequired(upload.metadata?.id_type, option.value) && !option.disabled,
      ),
    );
  }, [idsUploadedOnList, options]);

  // Use ref to track previous value and prevent unnecessary updates
  const prevUploadedIdsRef = useRef([]);

  // Save uploaded document values to form
  useEffect(() => {
    if (!setValue) return;

    const currentUploadedIds = idsUploadedOnList
      .map(upload => upload.metadata?.id_type)
      .filter(Boolean);

    // Only update if the values have actually changed
    const hasChanged =
      currentUploadedIds.length !== prevUploadedIdsRef.current.length ||
      currentUploadedIds.some((id, index) => id !== prevUploadedIdsRef.current[index]);

    if (hasChanged) {
      setValue(`${code}`, currentUploadedIds);
      prevUploadedIdsRef.current = currentUploadedIds;
    }
  }, [idsUploadedOnList, code, setValue]);

  const selfieId = useMemo(() => {
    return other_uploads?.find(u => u?.metadata?.selfie)?.id;
  }, [other_uploads]);

  return (
    <div
      className={cn(
        styles.root,
        { [className]: className },
        'd-flex flex-column u-gap-medium justify-content-center align-items-start u-padding',
        options.length > 0 && 'u-border-radius--medium u-border',
        wrapperRequirementsMet &&
          idsUploadedOnList?.length >= 0 &&
          !isVerifying &&
          'u-border-success',
        isVerifying && 'u-border-in-progress',
      )}>
      <div className="d-flex justify-content-between align-items-start u-width-100">
        <div className="d-flex flex-wrap justify-content-between align-items-start u-gap-medium u-width-100">
          <div className="d-flex flex-column u-gap-small u-width-75">
            <span className="primary-text u-font-size-small u-font-weight-500">{title}</span>
            {description && (
              <span className="secondary-text u-font-size-x-small u-font-weight-400 white-space-pre-line">
                {description}
              </span>
            )}
          </div>
          <div className="d-flex align-items-center u-gap">
            {wrapperRequirementsMet && hasIdRequirements && !isVerifying && (
              <StatusPill text="Requirements Met" status="success" />
            )}
            {!hasIdRequirements && <StatusPill text={'Optional'} status={'light'} />}
          </div>
        </div>
        {wrapperRequirementsMet && (
          <div className="d-flex">
            <IconComponent
              icon={showList ? ArrowUp01Icon : ArrowDown01Icon}
              noBackground
              type="secondary"
              size="small"
              strokeWidth={1.5}
              onClick={() => setShowList(!showList)}
            />
          </div>
        )}
      </div>
      {idsUploadedOnListNotDisabled && idsUploadedOnListNotDisabled?.length > 0 && (
        <div className="d-flex flex-column u-gap">
          <div className="d-flex u-gap-small flex-wrap">
            <StatusPill
              text={`${idsUploadedOnListNotDisabled?.length} document(s) uploaded`}
              status="neutral"
            />
          </div>
        </div>
      )}
      {min > 0 && hasIdRequirements && !requirementsMet && (
        <FormMessage
          message={
            options?.length > 1
              ? `You need to upload at least ${min} document(s) from this list`
              : `You need to upload the following document`
          }
          type="Neutral"
          noMargin
          closeable={false}
        />
      )}
      {showList &&
        options.map(option => {
          // Match by option.value so generic options (e.g. "passport") match uploads like "passport:AU"
          const matchingUploads =
            id_uploads?.filter(
              u => matchesRequired(u?.metadata?.id_type, option.value) && isAcceptedUpload(u),
            ) || [];
          // If this option is generic (no country), don't show an upload that a specific option in this list also matches
          const optionParsed = parseIdKey(option.value);
          const existing =
            optionParsed.countryCode != null
              ? matchingUploads
              : matchingUploads.filter(
                  upload =>
                    !options.some(
                      other =>
                        other !== option &&
                        parseIdKey(other.value).countryCode &&
                        matchesRequired(upload?.metadata?.id_type, other.value),
                    ),
                );
          const existingOrNull = existing?.length ? existing : null;

          // Matching uploads for component value props: include pending (not yet accepted) uploads
          // so that DualUploadComponent receives the new upload id and verification can run.
          // isAcceptedUpload excludes new uploads (no id_upload_detail yet), which would make
          // frontValue/backValue null and wipe DualUploadComponent's state, preventing verification.
          const matchingUploadsForComponent =
            id_uploads?.filter(u => matchesRequired(u?.metadata?.id_type, option.value)) || [];
          const existingForComponent =
            optionParsed.countryCode != null
              ? matchingUploadsForComponent
              : matchingUploadsForComponent.filter(
                  upload =>
                    !options.some(
                      other =>
                        other !== option &&
                        parseIdKey(other.value).countryCode &&
                        matchesRequired(upload?.metadata?.id_type, other.value),
                    ),
                );
          const existingOrNullForComponent = existingForComponent?.length
            ? existingForComponent
            : null;

          const hasBypassedFaceSimilarity = id_uploads?.find(
            u =>
              u?.id_upload_detail?.face_similarity_status === 'failed' &&
              u?.metadata?.attempts >= 3,
          );
          const isFileRejected = (existingOrNullForComponent ?? existingOrNull)?.some(file =>
            rejectedData?.files?.includes(file.id),
          );

          // Use existingOrNullForComponent for value props so pending uploads are passed to the component
          const frontUpload = existingOrNullForComponent?.[0] || null;
          const backUploadId = frontUpload?.id_upload_detail?.back_upload_id;
          const backUpload = backUploadId
            ? id_uploads?.find(u => u.id === backUploadId) || null
            : null;
          return !option.allowMultipleUploads ? (
            option.dual ? (
              <DualUploadComponent
                key={option?.value}
                uploadCode={uploadCode}
                selfieId={selfieId}
                brand={brand}
                type={option.idType}
                selected={option.selected}
                allowedTypesMode={ALLOWED_TYPES_MODE_ID}
                idType={option?.value}
                verifyDocument={option?.verifyDocument !== undefined ? option.verifyDocument : true}
                skipVerification={option?.skipVerification || false}
                reTakeSelfie={
                  candidateFields?.hiddenValues?.face_similarity_passed === false &&
                  !hasBypassedFaceSimilarity
                }
                documentTitle={option?.name}
                documentDescription={option?.description}
                additionalInstructions={option?.additionalInstructions}
                disabled={option?.disabled}
                countryCode={option?.countryCode}
                backConditions={option?.backConditions}
                expiryTolerance={option?.expiryTolerance}
                frontValue={frontUpload?.id || null}
                backValue={backUpload?.id || null}
                listCode={code}
                metadata={{
                  ...(option?.metadata || {}),
                  id_type: option?.value,
                  attempts: frontUpload?.metadata?.attempts || 0,
                }}
                modelParams={modelParams}
                uploadStatus={frontUpload?.id_upload_detail?.status}
                uploadError={frontUpload?.id_upload_detail?.reason_for_failure}
                displayFields={option?.inputFields}
                idValues={(() => {
                  const extracted = frontUpload?.id_upload_detail?.extracted_fields;
                  return extracted || {};
                })()}
                needsComplementaryDoc={option?.needsComplementaryDoc}
                complementaryInstructions={option?.complementaryInstructions}
                getUpload={getUpload}
                signUpload={signUpload}
                createUpload={createUpload}
                deleteUpload={deleteUpload}
                handleStateOnVerify={handleVerifyState}
                caption={option.points ? `${option.points} points` : ''}
                uploadId={frontUpload?.id}
                rejected={isFileRejected}
                confirmedByCand={frontUpload?.id_upload_detail?.confirmed_by_cand || false}
              />
            ) : (
              <DocumentUpload
                key={option.value}
                uploadCode={uploadCode}
                selfieId={selfieId}
                brand={brand}
                type={option.idType}
                selected={option.selected}
                allowedTypesMode={ALLOWED_TYPES_MODE_ID}
                idType={option?.value}
                verifyDocument={option?.verifyDocument !== undefined ? option.verifyDocument : true}
                skipVerification={option?.skipVerification || false}
                reTakeSelfie={
                  candidateFields?.hiddenValues?.face_similarity_passed === false &&
                  !hasBypassedFaceSimilarity
                }
                documentTitle={option?.name}
                documentDescription={option?.description}
                additionalInstructions={option?.additionalInstructions}
                disabled={option?.disabled}
                countryCode={option?.countryCode}
                expiryTolerance={option?.expiryTolerance}
                value={existingOrNullForComponent?.[0]?.id || null}
                listCode={code}
                metadata={{
                  ...(option?.metadata || {}),
                  id_type: option?.value,
                  attempts: existingOrNullForComponent?.[0]?.metadata?.attempts ?? 0,
                }}
                modelParams={modelParams}
                uploadStatus={existingOrNullForComponent?.[0]?.id_upload_detail?.status}
                uploadError={existingOrNullForComponent?.[0]?.id_upload_detail?.reason_for_failure}
                displayFields={option?.inputFields}
                idValues={(() => {
                  const extracted =
                    existingOrNullForComponent?.[0]?.id_upload_detail?.extracted_fields;
                  return extracted || {};
                })()}
                needsComplementaryDoc={option?.needsComplementaryDoc}
                complementaryInstructions={option?.complementaryInstructions}
                getUpload={getUpload}
                signUpload={signUpload}
                createUpload={createUpload}
                deleteUpload={deleteUpload}
                handleStateOnVerify={handleVerifyState}
                caption={option.points ? `${option.points} points` : ''}
                uploadId={existingOrNullForComponent?.[0]?.id}
                rejected={isFileRejected}
                confirmedByCand={
                  existingOrNullForComponent?.[0]?.id_upload_detail?.confirmed_by_cand || false
                }
              />
            )
          ) : (
            <MultipleDocumentUpload
              key={option?.value}
              brand={brand}
              idType={option?.value}
              allowedTypesMode={ALLOWED_TYPES_MODE_ID}
              metadata={option?.metadata || {}}
              documentTitle={option?.name}
              documentDescription={option?.description}
              additionalInstructions={option?.additionalInstructions}
              disabled={option?.disabled}
              value={existingOrNullForComponent || existingOrNull || []}
              modelParams={modelParams}
              listCode={code}
              verifyDocument={option?.verifyDocument !== undefined ? option.verifyDocument : true}
              skipVerification={option?.skipVerification || false}
              needsComplementaryDoc={option?.needsComplementaryDoc}
              complementaryInstructions={option?.complementaryInstructions}
              getUpload={getUpload}
              signUpload={signUpload}
              createUpload={createUpload}
              deleteUpload={deleteUpload}
              handleStateOnVerify={handleVerifyState}
              caption={option.points ? `${option.points} points` : ''}
            />
          );
        })}
    </div>
  );
};
